import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CheckCircle2, Clock, ArrowRight } from "lucide-react";
import { onImgError } from "../lib/imageFallback";
import { useT } from "../lib/i18n";

function fmtDate(d) {
  try { return new Date(d).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

export function FeaturedCarousel({ items = [] }) {
  const { t: tr } = useT();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const n = items.length;

  useEffect(() => {
    if (paused || reduced || n <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), 7000);
    return () => clearInterval(t);
  }, [paused, reduced, n]);

  if (n === 0) return null;
  const go = (d) => setIdx((i) => (i + d + n) % n);
  const a = items[idx];

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <section
      className="relative"
      data-testid="featured-carousel"
      aria-roledescription="carousel"
      aria-label="Featured stories"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="text-[11px] uppercase tracking-widest font-bold text-pti-red mb-3">Featured Story</div>

      <div key={idx} className="pti-slide-fade" aria-live="polite">
        <Link to={`/article/${a.slug}`} className="group block" data-testid={`carousel-slide-${idx}`}>
          <div className="aspect-video overflow-hidden bg-white relative">
            <img src={a.cover_image} alt={a.cover_alt || a.title} onError={onImgError} className="block w-full h-full object-contain object-center" />
          </div>
          <div className="pt-4">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold">
              <span className="text-pti-red">{a.category}</span>
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">{fmtDate(a.published_date)}</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black leading-tight mt-2 group-hover:text-pti-red transition-colors">{a.title}</h2>
            {a.excerpt && <p className="text-gray-600 mt-2 line-clamp-2 text-base">{a.excerpt}</p>}
          </div>
        </Link>

        <div className="mt-4 space-y-4">
          {a.author && (
            <Link to={`/creators/${a.author.username}`} className="flex items-center gap-2" data-testid="carousel-byline">
              <img src={a.author.avatar || "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=100"} alt="" className="w-9 h-9 rounded-full object-cover bg-gray-100" />
              <div className="leading-tight">
                <div className="text-sm font-bold flex items-center gap-1">{a.author.display_name}{a.author.verified && <CheckCircle2 size={12} className="text-pti-red" aria-label="Verified" />}</div>
                <div className="text-xs text-gray-500 flex items-center gap-2">@{a.author.username} <span className="flex items-center gap-1"><Clock size={11} /> {a.read_time} min</span></div>
              </div>
            </Link>
          )}
          <Link to={`/article/${a.slug}`} data-testid="carousel-read-btn" className="inline-flex w-full sm:w-auto justify-center sm:justify-start items-center gap-2 bg-pti-red text-white px-6 py-3 font-bold hover:bg-pti-redhover transition-colors">
            {tr("readStory")} <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      {/* Arrow controls */}
      {n > 1 && (
        <>
          <button onClick={() => go(-1)} data-testid="carousel-prev" aria-label="Previous story"
            className="absolute top-[calc(28.125%-20px)] -translate-y-1/2 left-3 bg-white/90 hover:bg-pti-red hover:text-white text-pti-black w-10 h-10 flex items-center justify-center rounded-full shadow-md transition-colors">
            <ChevronLeft size={22} />
          </button>
          <button onClick={() => go(1)} data-testid="carousel-next" aria-label="Next story"
            className="absolute top-[calc(28.125%-20px)] -translate-y-1/2 right-3 bg-white/90 hover:bg-pti-red hover:text-white text-pti-black w-10 h-10 flex items-center justify-center rounded-full shadow-md transition-colors">
            <ChevronRight size={22} />
          </button>
        </>
      )}

      {/* Slide indicators */}
      {n > 1 && (
        <div className="flex items-center gap-2 mt-4" role="tablist" aria-label="Choose slide">
          {items.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`carousel-dot-${i}`} role="tab" aria-selected={i === idx} aria-label={`Go to story ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-pti-red" : "w-2 bg-gray-300 hover:bg-gray-400"}`} />
          ))}
        </div>
      )}
    </section>
  );
}
